import * as React from "react";
import { ReactNode, useCallback, useEffect, useRef, useState } from "react";
import {
  DeepMap,
  FieldError,
  FieldValues,
  UseControllerProps,
  useController
} from "react-hook-form";

import {
  AutocompleteFreeSoloValueMapping,
  AutocompleteHighlightChangeReason,
  AutocompleteValue
} from "@mui/base/useAutocomplete/useAutocomplete";
import ExpandMoreOutlinedIcon from "@mui/icons-material/ExpandMoreOutlined";
import Autocomplete, { AutocompleteProps } from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";
import type { SxProps } from "@mui/material/styles";
import { FilterOptionsState } from "@mui/material/useAutocomplete";

import { get } from "lodash";

const EMPTY_ARRAY: any[] = [];

type DetailAutocompleteFormFieldProps<
  K,
  Multiple extends boolean | undefined = false,
  DisableClearable extends boolean | undefined = false,
  FreeSolo extends boolean | undefined = false
> = {
  name: string;
  label: string;
  autoFocus?: boolean;
  disableClearable?: DisableClearable;
  enablePortal?: boolean;
  placeholder?: string;
  disabled?: boolean;
  required?: boolean;
  multiple?: Multiple;
  disableCloseOnSelect?: boolean;
  options?: K[];
  noOptionsText?: string;
  isLoading?: boolean;
  getOptionLabel: (
    option: K | AutocompleteFreeSoloValueMapping<FreeSolo>
  ) => string;
  isOptionEqualToValue?: (option: K, value: K) => boolean;
  getOptionDisabled?: (option: K) => boolean;
  inputFormat?: string;
  popupIcon?: ReactNode;
  openOnFocus?: boolean;
  autoHighlight?: boolean;
  freeSolo?: FreeSolo;
  filterOptions?: (options: Array<K>, state: FilterOptionsState<K>) => Array<K>;
  onOptionSelected?: (
    option: AutocompleteValue<K, Multiple, DisableClearable, FreeSolo>,
    field?: string
  ) => void;
  transformValue?: (
    value: AutocompleteValue<K, Multiple, DisableClearable, FreeSolo>
  ) => AutocompleteValue<K, Multiple, DisableClearable, FreeSolo>;
  onTextInputChange?: (text: string) => void;
  blurOnSelect?: boolean;
  inputRef?: React.RefObject<HTMLInputElement>;
  onSubmit?: () => void;
  sx?: SxProps;
  containerSx?: SxProps;
  groupBy?: (option: K) => string;
  inputSx?: SxProps;
  /**
   * Override the Paper that wraps the listbox. Use to render footers (e.g.
   * "type to search for more" hints) inside the popper without getting
   * occluded by the listbox itself.
   */
  PaperComponent?: React.ElementType;
  /**
   * Node rendered inside the input, before the selected value's text (e.g. a
   * status chip for the chosen option). Single-select only: in `multiple`
   * mode MUI owns `startAdornment` for the tag list, so this is ignored there.
   */
  inputStartAdornment?: ReactNode;
  /**
   * Free-text fields only: whether text the user typed may be committed as a value when they
   * leave the field. Text that fails stays in the box, uncommitted (e.g. the email To field only
   * accepts what looks like an address, so a leftover search like "Bay" never becomes a chip).
   */
  canCommitTypedText?: (text: string) => boolean;
};

export type RhfDetailAutocompleteFormFieldProps<
  K,
  T extends FieldValues,
  Multiple extends boolean | undefined = false,
  DisableClearable extends boolean | undefined = false,
  FreeSolo extends boolean | undefined = false
> = DetailAutocompleteFormFieldProps<K, Multiple, DisableClearable, FreeSolo> &
  UseControllerProps<T> &
  Pick<
    AutocompleteProps<K, Multiple, DisableClearable, FreeSolo>,
    | "renderOption"
    | "ListboxComponent"
    | "renderTags"
    | "limitTags"
    // Passthroughs to MUI. Both default to undefined, so the popup stays
    // uncontrolled for every caller that does not pass them. Callers that need
    // to know when the listbox is open use these (e.g. to hold off a
    // side-effecting read until the user is done picking).
    | "onOpen"
    | "onClose"
  >;

export default function AutocompleteFormField<
  K,
  T extends FieldValues,
  Multiple extends boolean | undefined = false,
  DisableClearable extends boolean | undefined = false,
  FreeSolo extends boolean | undefined = false
>({
  name,
  label,
  autoFocus,
  disableClearable,
  enablePortal = false,
  multiple,
  freeSolo,
  disabled = false,
  disableCloseOnSelect,
  control,
  required = false,
  options = [],
  getOptionLabel,
  isOptionEqualToValue,
  getOptionDisabled,
  renderOption,
  renderTags,
  limitTags,
  isLoading = false,
  noOptionsText,
  popupIcon = <ExpandMoreOutlinedIcon />,
  filterOptions,
  onOptionSelected,
  transformValue,
  onTextInputChange,
  onSubmit,
  openOnFocus,
  autoHighlight,
  placeholder,
  groupBy,
  blurOnSelect,
  inputRef: inputRefProp,
  ListboxComponent,
  rules = {},
  sx = { ml: 2, mt: 1 },
  inputSx = {},
  containerSx,
  PaperComponent,
  onOpen,
  onClose,
  inputStartAdornment,
  canCommitTypedText
}: RhfDetailAutocompleteFormFieldProps<
  K,
  T,
  Multiple,
  DisableClearable,
  FreeSolo
>) {
  const internalInputRef = useRef<HTMLInputElement>(null);
  const inputRef = inputRefProp ?? internalInputRef;
  const [highlight, setHighlight] = useState<K | null>(null);
  // Read by the free-text blur commit below. Refs, not state: they are only read inside the blur
  // handler, and MUI fires its blur before React would re-render with new state anyway.
  const highlightRef = useRef<{
    option: K | null;
    reason: AutocompleteHighlightChangeReason | null;
  }>({ option: null, reason: null });
  const isPopupOpenRef = useRef(false);
  // A free-text MULTI-select owns its input text. MUI clears the text after a pick it made itself,
  // but not after the blur commit below, which would leave the committed address in the box for
  // the next one to be typed onto. Single-selects stay uncontrolled: their text IS the value.
  const ownsInputText = Boolean(freeSolo && multiple);
  const [inputText, setInputText] = useState("");

  useEffect(() => {
    autoFocus && inputRef.current?.focus();
  }, [autoFocus, inputRef]);

  const {
    field: { onChange, value: fieldValue, ...restField },
    formState: { errors }
  } = useController<T>({
    name,
    control,
    rules: {
      required: {
        value: required,
        message: `${label || "Value"} is required`
      },
      ...rules
    }
  });

  const errorMessage =
    get(errors, name) &&
    `${(get(errors, name) as DeepMap<FieldValues, FieldError>).message}`;

  const keepOpenOnSelect = disableCloseOnSelect ?? Boolean(multiple);

  type Value = AutocompleteValue<K, Multiple, DisableClearable, FreeSolo>;

  const commitValue = (data: Value) => {
    const transformed = transformValue ? transformValue(data) : data;
    onChange(transformed);
    if (onOptionSelected) {
      onOptionSelected(transformed, name);
    }
  };

  // Whether a candidate is already in a multi-select's value. A typed string can equal an option
  // object (the To field compares a typed address with a chip's email), so the caller's
  // comparison sees mixed pairs too. Without one, labels decide: the values RHF hands back are
  // copies, so `===` never matches an option object.
  const isSameItem = (a: unknown, b: unknown) => {
    if (a === b) return true;
    if (isOptionEqualToValue) {
      try {
        return isOptionEqualToValue(a as K, b as K);
      } catch {
        return false;
      }
    }
    return (
      getOptionLabel(a as K | AutocompleteFreeSoloValueMapping<FreeSolo>) ===
      getOptionLabel(b as K | AutocompleteFreeSoloValueMapping<FreeSolo>)
    );
  };

  // Set while MUI's own pick is being applied. A single-select blurs its input inside that same
  // pick (blurOnSelect), before React re-renders, so the blur handler would still read the typed
  // filter ("utm") and the old value, and overwrite the option just picked.
  const justPickedRef = useRef(false);

  // Commits what the user chose: an option reached with the arrow keys, else the text they typed.
  // Never a row that is highlighted only because the mouse rests on it.
  const commitChoice = (rawText: string) => {
    const { option: highlighted, reason } = highlightRef.current;
    const typed = multiple ? rawText.trim() : rawText;
    const useHighlighted =
      isPopupOpenRef.current && highlighted != null && reason === "keyboard";
    if (!useHighlighted && canCommitTypedText && !canCommitTypedText(typed)) {
      return;
    }
    const candidate: unknown = useHighlighted ? highlighted : typed;
    if (candidate === "" || candidate == null) return;

    if (multiple) {
      const current = (
        Array.isArray(fieldValue) ? fieldValue : []
      ) as unknown[];
      if (current.some((item) => isSameItem(candidate, item))) {
        setInputText("");
        return;
      }
      commitValue([...current, candidate] as Value);
      setInputText("");
    } else {
      if (candidate === fieldValue) return;
      // Leaving a field that still shows the picked option's own text keeps that option.
      if (
        typeof candidate === "string" &&
        fieldValue != null &&
        candidate === getOptionLabel(fieldValue)
      ) {
        return;
      }
      commitValue(candidate as Value);
    }
  };

  // Free-text fields commit on blur here instead of through MUI's `autoSelect` (SN-19878).
  // `autoSelect` commits whichever option is HIGHLIGHTED first, and resting the mouse on a row
  // highlights it. Since multi-selects stay open after a pick (ENG-2215), the mouse is usually
  // resting on some row, so switching tabs, taking a screenshot or clicking into another field
  // added that row unasked — on the email To field, a second patient.
  // Nothing commits when the whole window lost focus; the typed text stays in the box and is
  // committed on the next real blur.
  const handleFreeSoloBlur = (event: React.FocusEvent<HTMLInputElement>) => {
    if (!freeSolo || justPickedRef.current) return;
    if (typeof document !== "undefined" && !document.hasFocus()) return;
    const related = event.relatedTarget as Element | null;
    // Focus moving into the listbox (MUI refocuses the input) is not leaving the field.
    if (related?.closest?.('[role="listbox"]')) return;
    commitChoice(event.target.value);
  };

  // Enter has the same trap as blur: MUI picks whichever row is highlighted, and the mouse resting
  // on the list highlights one, so typing an address and pressing Enter added the hovered row
  // instead (or, when that row was already picked, removed it). MUI is left to handle Enter only
  // for a row the user just reached with the arrow keys, or for plain typed text it may commit.
  const handleFreeSoloEnter = (
    event: React.KeyboardEvent<HTMLInputElement>
  ) => {
    if (!freeSolo || event.key !== "Enter") return;
    // Enter that confirms an IME composition is not a submit; MUI ignores it too.
    if (event.nativeEvent.isComposing || event.which === 229) return;
    const input = event.target as HTMLInputElement;
    const { option: highlighted, reason } = highlightRef.current;
    const isKeyboardPick =
      isPopupOpenRef.current && highlighted != null && reason === "keyboard";
    if (isKeyboardPick) return;
    // MUI's own record of the highlighted row; it can keep one across a close and reopen without
    // telling us, so our ref alone cannot say whether MUI is about to pick a row.
    const muiWouldPickRow =
      isPopupOpenRef.current && input.hasAttribute("aria-activedescendant");
    const typed = multiple ? input.value.trim() : input.value;
    const typedRefused =
      typed !== "" &&
      canCommitTypedText !== undefined &&
      !canCommitTypedText(typed);
    if (!muiWouldPickRow && !typedRefused) return;

    (
      event as React.KeyboardEvent & { defaultMuiPrevented?: boolean }
    ).defaultMuiPrevented = true;
    if (multiple || muiWouldPickRow) event.preventDefault();
    commitChoice(input.value);
  };

  const handleOpen = useCallback(
    (event: React.SyntheticEvent) => {
      isPopupOpenRef.current = true;
      onOpen?.(event);
    },
    [onOpen]
  );

  const handleClose = useCallback(
    (
      event: React.SyntheticEvent,
      reason: Parameters<NonNullable<typeof onClose>>[1]
    ) => {
      isPopupOpenRef.current = false;
      // An arrow-key highlight means nothing once the list is closed (MUI may keep it silently).
      highlightRef.current = { option: null, reason: null };
      onClose?.(event, reason);
    },
    [onClose]
  );

  return (
    <Autocomplete<K, Multiple, DisableClearable, FreeSolo>
      freeSolo={freeSolo}
      disableClearable={disableClearable}
      openOnFocus={openOnFocus}
      autoHighlight={autoHighlight}
      autoSelect={false}
      blurOnSelect={blurOnSelect ?? !keepOpenOnSelect}
      disablePortal={!enablePortal}
      onOpen={handleOpen}
      onClose={handleClose}
      multiple={multiple}
      disableCloseOnSelect={keepOpenOnSelect}
      disabled={disabled}
      options={options}
      loading={isLoading}
      noOptionsText={noOptionsText}
      fullWidth
      ListboxComponent={ListboxComponent}
      PaperComponent={PaperComponent}
      popupIcon={popupIcon}
      getOptionLabel={getOptionLabel}
      isOptionEqualToValue={isOptionEqualToValue}
      getOptionDisabled={getOptionDisabled}
      renderTags={renderTags}
      limitTags={limitTags}
      renderOption={renderOption}
      {...(ownsInputText
        ? {
            inputValue: inputText,
            onInputChange: (_: React.SyntheticEvent, text: string) =>
              setInputText(text)
          }
        : {})}
      onHighlightChange={(_, newHighlight, reason) => {
        highlightRef.current = { option: newHighlight, reason };
        setHighlight(newHighlight);
      }}
      renderInput={({ InputProps: inputProps, ...restParams }) => {
        const InputProps =
          !multiple && inputStartAdornment
            ? { ...inputProps, startAdornment: inputStartAdornment }
            : inputProps;

        return (
          <TextField
            sx={sx}
            {...restParams}
            inputProps={{
              ...restParams.inputProps,
              onBlur: (event: React.FocusEvent<HTMLInputElement>) => {
                handleFreeSoloBlur(event);
                restParams.inputProps.onBlur?.(event);
              }
            }}
            InputLabelProps={{
              ...restParams.InputLabelProps,
              disabled: false
            }}
            InputProps={{
              placeholder: placeholder,
              ...(disabled
                ? { ...InputProps, disableUnderline: disabled }
                : InputProps),
              onKeyDown: (e) => {
                handleFreeSoloEnter(e as React.KeyboardEvent<HTMLInputElement>);
                if (!freeSolo && !highlight && e.key === "Enter") {
                  e.stopPropagation();
                }
                if (e.key === "Enter") {
                  onSubmit?.();
                }
              },
              sx: inputSx
            }}
            required={required}
            error={Boolean(errorMessage)}
            helperText={errorMessage || ""}
            disabled={disabled}
            label={label}
            onChange={(event) => {
              // Typing makes any earlier arrow-key choice stale: Enter or leaving the field now
              // means the typed text, unless the user arrows to a row again.
              highlightRef.current = {
                option: highlightRef.current.option,
                reason: null
              };
              if (onTextInputChange) {
                onTextInputChange(event.target.value);
              }
            }}
            inputRef={inputRef}
          />
        );
      }}
      groupBy={groupBy}
      onChange={(_, data) => {
        justPickedRef.current = true;
        // A pick uses up an arrow-key highlight: leaving the field afterwards must not add it again.
        highlightRef.current = {
          option: highlightRef.current.option,
          reason: null
        };
        queueMicrotask(() => {
          justPickedRef.current = false;
        });
        commitValue(data);
      }}
      sx={containerSx}
      {...restField}
      value={multiple && fieldValue == null ? EMPTY_ARRAY : fieldValue}
      {...(filterOptions ? { filterOptions } : { filterOptions: (x) => x })}
    />
  );
}
